import { createTheme } from "@mui/material/styles";

// ─────────────────────────────────────────────────────────────────────────────
// DARK tokens  —  "Midnight Blue" premium night palette
// ─────────────────────────────────────────────────────────────────────────────

export const DK = {
  // ── Backgrounds ──────────────────────────────────────────────────────────
  appBg:   "#0a0e1a",          // deep midnight blue (was pure black)
  s0:      "#0f1419",          // card / sidebar base (blue-tinted)
  s1:      "#141a24",          // slightly raised surface (more blue)
  s2:      "#1a2332",          // hover / input fill (readable blue)
  s3:      "#202b3f",          // selected / pressed (brighter blue)
  s4:      "#283449",          // deepest accent bg (visible blue)

  // ── Borders ───────────────────────────────────────────────────────────────
  border:  "rgba(99,143,255,0.09)",     // subtle blue tint
  borderHov: "rgba(99,143,255,0.18)",   // brighter blue on hover

  // ── Text ──────────────────────────────────────────────────────────────────
  muted:     "#4a5a7a",        // slightly lighter for better readability
  secondary: "#8294b8",        // improved contrast on blue bg
  primary:   "#b8c8e8",        // clearer on blue backgrounds
  heading:   "#e3ecff",        // crisp white-blue for headers

  // ── Semantic accents ──────────────────────────────────────────────────────
  blue:    "#3b82f6",
  blueHov: "#2563eb",
  green:   "#10b981",
  amber:   "#f59e0b",
  red:     "#ef4444",
  violet:  "#8b5cf6",
  cyan:    "#06b6d4",

  // ── Alpha fills ───────────────────────────────────────────────────────────
  greenAlpha:   "rgba(16,185,129,0.10)",
  amberAlpha:   "rgba(245,158,11,0.10)",
  redAlpha:     "rgba(239,68,68,0.10)",
  blueAlpha:    "rgba(59,130,246,0.10)",
  violetAlpha:  "rgba(139,92,246,0.10)",
  neutralAlpha: "rgba(176,190,216,0.07)",

  // ── Glow shadows (box-shadow strings) ─────────────────────────────────────
  glowBlue:   "0 0 0 1px rgba(59,130,246,0.30), 0 0 14px rgba(59,130,246,0.15)",
  glowGreen:  "0 0 0 1px rgba(16,185,129,0.24), 0 0 12px rgba(16,185,129,0.12)",
  cardShadow: "0 2px 4px rgba(0,0,0,0.3), 0 8px 20px rgba(10,14,26,0.4)",
  elevatedShadow: "0 4px 24px rgba(0,0,0,0.5), 0 2px 4px rgba(10,14,26,0.3)",

  // ── Sidebar gradient for active item ──────────────────────────────────────
  navActiveBg: "linear-gradient(90deg, rgba(59,130,246,0.16) 0%, rgba(59,130,246,0.06) 100%)",
};

// ─────────────────────────────────────────────────────────────────────────────
// LIGHT tokens  —  "Glacier" clean crisp palette
// ─────────────────────────────────────────────────────────────────────────────

export const LT = {
  // ── Backgrounds ──────────────────────────────────────────────────────────
  appBg:   "#f0f3f9",
  s0:      "#ffffff",
  s1:      "#f8fafd",
  s2:      "#f0f3f9",
  s3:      "#e4eaf4",
  s4:      "#dae2f0",

  // ── Borders ───────────────────────────────────────────────────────────────
  border:  "#dde3ef",
  borderHov: "#b8c5de",

  // ── Text ──────────────────────────────────────────────────────────────────
  muted:     "#94a3be",
  secondary: "#5e7099",
  primary:   "#2d3f5e",
  heading:   "#0e1c36",

  // ── Semantic accents (same as dark for brand consistency) ─────────────────
  blue:    "#2563eb",
  blueHov: "#1d4ed8",
  green:   "#10b981",
  amber:   "#f59e0b",
  red:     "#ef4444",
  violet:  "#7c3aed",
  cyan:    "#0891b2",

  // ── Alpha fills ───────────────────────────────────────────────────────────
  greenAlpha:   "rgba(16,185,129,0.08)",
  amberAlpha:   "rgba(245,158,11,0.08)",
  redAlpha:     "rgba(239,68,68,0.08)",
  blueAlpha:    "rgba(37,99,235,0.08)",
  violetAlpha:  "rgba(124,58,237,0.08)",
  neutralAlpha: "rgba(94,112,153,0.07)",

  // ── Shadows ───────────────────────────────────────────────────────────────
  glowBlue:   "0 0 0 1px rgba(37,99,235,0.20), 0 0 8px rgba(37,99,235,0.08)",
  glowGreen:  "0 0 0 1px rgba(16,185,129,0.18), 0 0 6px rgba(16,185,129,0.06)",
  cardShadow: "0 1px 3px rgba(14,28,54,0.06), 0 4px 12px rgba(14,28,54,0.04)",
  elevatedShadow: "0 4px 24px rgba(14,28,54,0.10), 0 1px 3px rgba(14,28,54,0.06)",

  navActiveBg: "linear-gradient(90deg, rgba(37,99,235,0.10) 0%, rgba(37,99,235,0.03) 100%)",
};

// ─────────────────────────────────────────────────────────────────────────────
// Token getter
// ─────────────────────────────────────────────────────────────────────────────

export const T = (mode) => (mode === "dark" ? DK : LT);

// ─────────────────────────────────────────────────────────────────────────────
// Chip overrides factory
// ─────────────────────────────────────────────────────────────────────────────

const chipOverrides = (tok) => ({
  root: {
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 600,
    height: 20,
    letterSpacing: "0.04em",
  },
  colorSuccess:  {
    backgroundColor: tok.greenAlpha,
    color: tok.mode === "dark" ? "#34d399" : "#059669",
    border: `0.5px solid ${tok.green}35`,
  },
  colorWarning:  {
    backgroundColor: tok.amberAlpha,
    color: tok.mode === "dark" ? "#fbbf24" : "#d97706",
    border: `0.5px solid ${tok.amber}35`,
  },
  colorError:    {
    backgroundColor: tok.redAlpha,
    color: tok.mode === "dark" ? "#f87171" : "#dc2626",
    border: `0.5px solid ${tok.red}35`,
  },
  colorInfo:     {
    backgroundColor: tok.blueAlpha,
    color: tok.mode === "dark" ? "#93c5fd" : "#2563eb",
    border: `0.5px solid ${tok.blue}35`,
  },
  colorPrimary:  {
    backgroundColor: tok.blueAlpha,
    color: tok.mode === "dark" ? "#93c5fd" : "#2563eb",
    border: `0.5px solid ${tok.blue}35`,
  },
  colorSecondary:{
    backgroundColor: tok.violetAlpha,
    color: tok.mode === "dark" ? "#c4b5fd" : "#7c3aed",
    border: `0.5px solid ${tok.violet}35`,
  },
  colorDefault:  {
    backgroundColor: tok.neutralAlpha,
    color: tok.secondary,
    border: `0.5px solid ${tok.border}`,
  },
  filled: { border: "none" },
});

// ─────────────────────────────────────────────────────────────────────────────
// Theme factory
// ─────────────────────────────────────────────────────────────────────────────

export function buildAdminTheme(mode) {
  const tok    = mode === "dark" ? DK : LT;
  const isDark = mode === "dark";
  tok.mode = mode;

  // Suitable tab / toggle button colours for both themes.
  // These are separate from the fixed blue sidebar.
  const tabAccent = isDark ? "#7dd3fc" : "#0369a1";
  const tabHoverBg = isDark ? "rgba(56,189,248,0.12)" : "rgba(3,105,161,0.08)";
  const tabActiveBg = isDark ? "rgba(56,189,248,0.18)" : "rgba(3,105,161,0.12)";
  const tabActiveBgHover = isDark ? "rgba(56,189,248,0.24)" : "rgba(3,105,161,0.16)";
  const tabActiveBorder = isDark ? "rgba(125,211,252,0.45)" : "rgba(3,105,161,0.35)";

  return createTheme({
    palette: {
      mode,
      background: { default: tok.appBg, paper: tok.s0 },
      text: { primary: tok.primary, secondary: tok.secondary, disabled: tok.muted },
      divider: tok.border,
      primary:   { main: tok.blue,   dark: tok.blueHov, contrastText: "#fff" },
      success:   { main: tok.green,  contrastText: "#fff" },
      warning:   { main: tok.amber,  contrastText: isDark ? "#fff" : "#451a03" },
      error:     { main: tok.red,    contrastText: "#fff" },
      secondary: { main: tok.violet, contrastText: "#fff" },
      action: {
        hover:              isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
        selected:           tok.s3,
        disabled:           tok.muted,
        disabledBackground: tok.s2,
      },
    },

    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontWeightRegular: 400,
      fontWeightMedium:  500,
      fontWeightBold:    600,
      h1: { fontSize: 24, fontWeight: 600, color: tok.heading, letterSpacing: "-0.02em" },
      h2: { fontSize: 17, fontWeight: 600, color: tok.heading, letterSpacing: "-0.01em" },
      h3: { fontSize: 15, fontWeight: 500, color: tok.heading },
      h4: { fontSize: 13, fontWeight: 500, color: tok.primary },
      body1: { fontSize: 13, color: tok.primary, lineHeight: 1.6 },
      body2: { fontSize: 12, color: tok.secondary, lineHeight: 1.5 },
      caption: {
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: tok.muted,
      },
      subtitle1: { fontSize: 15, fontWeight: 600, color: tok.heading },
      subtitle2: { fontSize: 13, fontWeight: 500, color: tok.primary },
    },

    shape: { borderRadius: 10 },

    components: {

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tok.s0,
            border: `0.5px solid ${tok.border}`,
            boxShadow: "none",
            transition: "background-color 0.2s ease, border-color 0.2s ease",
          },
          outlined: { border: `0.5px solid ${tok.border}` },
          elevation1: { boxShadow: tok.cardShadow },
          elevation2: { boxShadow: tok.elevatedShadow },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tok.s0,
            border: `0.5px solid ${tok.border}`,
            boxShadow: isDark ? tok.cardShadow : "none",
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: tok.s1,
            border: `0.5px solid ${tok.borderHov}`,
            boxShadow: tok.elevatedShadow,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: tok.heading,
            fontSize: 15,
            fontWeight: 600,
            padding: "16px 18px",
            borderBottom: `0.5px solid ${tok.border}`,
            letterSpacing: "-0.01em",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: { root: { padding: "18px" } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "12px 18px",
            borderTop: `0.5px solid ${tok.border}`,
            gap: 8,
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? tok.s1 : tok.s1,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: isDark ? tok.s1 : tok.s1,
            color: tok.muted,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            borderBottom: `0.5px solid ${tok.border}`,
            padding: "10px 14px",
            userSelect: "none",
          },
          body: {
            color: tok.primary,
            fontSize: 12.5,
            borderBottom: `0.5px solid ${tok.border}`,
            padding: "11px 14px",
            verticalAlign: "middle",
            lineHeight: 1.5,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background-color 0.12s ease",
            "&:hover": { backgroundColor: tok.s2 },
            "&:last-child td": { borderBottom: "none" },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { backgroundColor: "transparent" } },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: tok.s2,
            fontSize: 13,
            borderRadius: 8,
            transition: "box-shadow 0.15s ease, background-color 0.15s ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tok.border,
              transition: "border-color 0.15s ease",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tok.borderHov },
            "&.Mui-focused": {
              backgroundColor: tok.s1,
              boxShadow: tok.glowBlue,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: tok.blue,
                borderWidth: 1,
              },
            },
          },
          input: {
            color: tok.primary,
            "&::placeholder": { color: tok.muted, opacity: 1 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tok.muted,
            fontSize: 13,
            "&.Mui-focused": { color: tok.blue },
          },
        },
      },
      MuiSelect: {
        styleOverrides: { icon: { color: tok.muted } },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 8,
            boxShadow: "none",
            transition: "all 0.15s ease",
            "&:hover": { boxShadow: "none", transform: "translateY(-0.5px)" },
            "&:active": { transform: "translateY(0)" },
          },
          contained: {
            backgroundColor: tok.blue,
            color: "#fff",
            boxShadow: isDark
              ? "0 1px 2px rgba(0,0,0,0.4), 0 0 0 1px rgba(59,130,246,0.3)"
              : "0 1px 3px rgba(37,99,235,0.25)",
            "&:hover": {
              backgroundColor: tok.blueHov,
              boxShadow: isDark
                ? "0 2px 8px rgba(59,130,246,0.25), 0 0 0 1px rgba(59,130,246,0.4)"
                : "0 2px 8px rgba(37,99,235,0.30)",
            },
          },
          outlined: {
            borderColor: tok.border,
            color: tok.secondary,
            "&:hover": {
              borderColor: tok.borderHov,
              backgroundColor: tok.s2,
              color: tok.primary,
            },
          },
          text: {
            color: tok.secondary,
            "&:hover": { backgroundColor: tok.s2, color: tok.primary },
          },
          containedError: {
            backgroundColor: tok.redAlpha,
            color: isDark ? "#f87171" : "#dc2626",
            border: `0.5px solid ${tok.red}40`,
            boxShadow: "none",
            "&:hover": { backgroundColor: `${tok.red}18` },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: tok.secondary,
            transition: "all 0.15s ease",
            "&:hover": { backgroundColor: tok.s2, color: tok.primary },
          },
        },
      },

      MuiChip: { styleOverrides: chipOverrides(tok) },

      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: tok.s1,
            border: `0.5px solid ${tok.borderHov}`,
            boxShadow: tok.elevatedShadow,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13,
            color: tok.primary,
            borderRadius: 6,
            margin: "1px 4px",
            transition: "background-color 0.12s ease",
            "&:hover": { backgroundColor: tok.s2 },
            "&.Mui-selected": {
              backgroundColor: tok.s3,
              color: tok.heading,
              "&:hover": { backgroundColor: tok.s3 },
            },
          },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: tok.border } },
      },

      MuiAlert: {
        styleOverrides: {
          root: { border: "0.5px solid", borderRadius: 10, fontSize: 13, fontWeight: 500 },
          filledSuccess:   { backgroundColor: tok.greenAlpha,  color: isDark ? "#34d399" : "#059669", borderColor: `${tok.green}35` },
          filledError:     { backgroundColor: tok.redAlpha,    color: isDark ? "#f87171" : "#dc2626", borderColor: `${tok.red}35`   },
          filledWarning:   { backgroundColor: tok.amberAlpha,  color: isDark ? "#fbbf24" : "#d97706", borderColor: `${tok.amber}35` },
          filledInfo:      { backgroundColor: tok.blueAlpha,   color: isDark ? "#93c5fd" : "#2563eb", borderColor: `${tok.blue}35`  },
          standardSuccess: { backgroundColor: tok.greenAlpha,  color: isDark ? "#34d399" : "#059669", borderColor: `${tok.green}35` },
          standardError:   { backgroundColor: tok.redAlpha,    color: isDark ? "#f87171" : "#dc2626", borderColor: `${tok.red}35`   },
          standardWarning: { backgroundColor: tok.amberAlpha,  color: isDark ? "#fbbf24" : "#d97706", borderColor: `${tok.amber}35` },
          standardInfo:    { backgroundColor: tok.blueAlpha,   color: isDark ? "#93c5fd" : "#2563eb", borderColor: `${tok.blue}35`  },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 99,
            backgroundColor: tabAccent,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            color: tok.secondary,
            minHeight: 36,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            textTransform: "none",
            transition: "all 0.15s ease",
            "&:hover": {
              color: tabAccent,
              backgroundColor: tabHoverBg,
            },
            "&.Mui-selected": {
              color: tabAccent,
              backgroundColor: tabActiveBg,
              fontWeight: 800,
            },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: tok.secondary,
            border: `0.5px solid ${tok.border}`,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 8,
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: tabHoverBg,
              color: tabAccent,
              borderColor: tabActiveBorder,
            },
            "&.Mui-selected": {
              backgroundColor: tabActiveBg,
              color: tabAccent,
              borderColor: tabActiveBorder,
              boxShadow: isDark
                ? "0 0 0 1px rgba(125,211,252,0.12), inset 0 1px 0 rgba(255,255,255,0.06)"
                : "0 0 0 1px rgba(3,105,161,0.08)",
              "&:hover": {
                backgroundColor: tabActiveBgHover,
                borderColor: tabActiveBorder,
              },
            },
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? tok.s4 : tok.s0,
            border: `0.5px solid ${tok.borderHov}`,
            color: tok.primary,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 7,
            boxShadow: tok.elevatedShadow,
            padding: "5px 10px",
          },
          arrow: { color: isDark ? tok.s4 : tok.s0 },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: tok.s3,
            color: tok.secondary,
            border: `0.5px solid ${tok.border}`,
          },
        },
      },

      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            scrollbarColor: isDark
              ? `${tok.s3} transparent`
              : `${tok.s3} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: tok.border,
              borderRadius: 3,
              "&:hover": { background: tok.borderHov },
            },
          },
        },
      },

      MuiFade: { defaultProps: { timeout: 180 } },
    },
  });
}

// Pre-built instances
export const adminDarkTheme  = buildAdminTheme("dark");
export const adminLightTheme = buildAdminTheme("light");